import React from 'react';
import moment from 'moment';
import { states } from '../../../constants/states';
import Card from '../../Card';
import { IReportDatePicker } from '../../inputs/DateTimePicker';
import { Dropdown, Textbox, TwoColumn } from '../../inputs';
import { EmployeeDropdown } from '../../inputs/Dropdown/employee';
import RadioButtons from '../../inputs/RadioButtons';
import { normalizePhone } from '../../../utils/formValidation';
import Button from '../../inputs/Button';

import styles from './claimCard.module.scss';

const getDropdownAnswer = (options, value) =>
  options?.find(o => o.value === value)?.label || '-';

const formatDate = date => (date ? moment(date).format('MM/DD/YYYY') : null);

const PHONE_TYPES = [
  { label: 'Mobile', value: 'M' },
  { label: 'Home', value: 'H' },
  { label: 'Work', value: 'W' }
];

const VEHICLE_TYPES = [
  { label: 'Auto', value: 'A' },
  { label: 'Truck', value: 'T' },
  { label: 'Van', value: 'V' },
  { label: 'SUV', value: 'S' },
  { label: 'Motorcycle', value: 'M' },
  { label: 'Other', value: 'O' }
];

const OWNERSHIP_TYPES = [
  { label: 'Owned', value: 'O' },
  { label: 'Leased', value: 'L' },
  { label: 'Rented', value: 'R' },
  { label: 'Non-Owned', value: 'N' }
];

const CLAIMANT_TYPES = [
  { label: 'Property Damage', value: 'P' },
  { label: 'Bodily Injury', value: 'B' },
  { label: 'Other Vehicle', value: 'A' }
];

const INJURY_TYPES = [
  { label: 'No Injury', value: '00' },
  { label: 'Minor Injury - No Treatment', value: '01' },
  { label: 'Minor Injury - Treatment at Scene', value: '02' },
  { label: 'Injury - ER Visit', value: '07' },
  { label: 'Injury - Hospitalized', value: '10' },
  { label: 'Serious Injury', value: '20' },
  { label: 'Fatal', value: '28' }
];

const RELATIONSHIP_TO_INSURED = [
  { label: 'Passenger', value: 'P' },
  { label: 'Pedestrian', value: 'D' },
  { label: 'Other Driver', value: 'O' },
  { label: 'Other', value: 'X' }
];

const SINGLE_VEHICLE_ACCIDENT_TYPES = [
  { label: 'Accident involving Animal', value: 'A' },
  { label: 'Accident with a Pedestrian or Bicyclist', value: '2' },
  { label: 'Accident with an Object', value: 'O' },
  { label: 'Damage from Flooding', value: 'E' },
  { label: 'Hit and Run Accident', value: '7' },
  { label: 'Damage from Fire', value: '5' },
  { label: 'Theft of Motor Vehicle or Parts', value: '6' },
  { label: 'Rollover Accident', value: '9' },
  { label: 'Vandalism', value: 'K' },
  { label: 'Weather (Non Hail)', value: 'L' },
  { label: 'Hail Damage', value: 'G' },
  { label: 'All Other Damages', value: '3' }
];

export default function TravelersAutoClaimCard({
  name,
  onChange,
  editing,
  claimSection,
  title,
  openEdit,
  isSubmitted,
  viewOnly,
  missingRequired,
  dateOfLoss,
  setDateOfLoss,
  locationCode,
  setLocationCode,
  locationCodes,
  permissionUsers
}) {
  return (
    <Card
      showHeader
      title={title}
      name={name}
      rightButton={{
        text: 'Edit',
        visible: !editing && !isSubmitted && !viewOnly,
        color: 'blue',
        onClick: () => openEdit(true)
      }}
    >
      {title === 'Contact Information' ? (
        <ContactInfoCard
          onChange={onChange}
          claimSection={claimSection}
          editing={editing}
          missingRequired={missingRequired}
          permissionUsers={permissionUsers}
        />
      ) : title === 'Loss Location' ? (
        <LossLocationCard
          onChange={onChange}
          claimSection={claimSection}
          editing={editing}
          missingRequired={missingRequired}
        />
      ) : title === 'Loss Details' ? (
        <LossDetailsCard
          onChange={onChange}
          claimSection={claimSection}
          editing={editing}
          missingRequired={missingRequired}
          dateOfLoss={dateOfLoss}
          setDateOfLoss={setDateOfLoss}
          locationCode={locationCode}
          setLocationCode={setLocationCode}
          locationCodes={locationCodes}
        />
      ) : title === 'Primary Vehicle' ? (
        <PrimaryVehicleCard
          onChange={onChange}
          claimSection={claimSection}
          editing={editing}
          missingRequired={missingRequired}
        />
      ) : title === 'Driver Information' ? (
        <DriverInfoCard
          onChange={onChange}
          claimSection={claimSection}
          editing={editing}
          missingRequired={missingRequired}
        />
      ) : title === 'Claimants' ? (
        <ClaimantsCard
          onChange={onChange}
          claimSection={claimSection}
          editing={editing}
          missingRequired={missingRequired}
        />
      ) : (
        <OverviewCard claimSection={claimSection} />
      )}
    </Card>
  );
}

function OverviewCard({ claimSection }) {
  return (
    <div className={styles.viewContainer}>
      <div className={styles.viewRow}>
        <span className={styles.label}>Claim Number:</span>
        <span className={styles.value}>{claimSection?.claimNumber || '-'}</span>
      </div>
      <div className={styles.viewRow}>
        <span className={styles.label}>Submission Date:</span>
        <span className={styles.value}>
          {formatDate(claimSection?.submissionDate) || '-'}
        </span>
      </div>
      <div className={styles.viewRow}>
        <span className={styles.label}>iReportSource Number:</span>
        <span className={styles.value}>
          {claimSection?.insuredReportNumber || '-'}
        </span>
      </div>
    </div>
  );
}

function ContactInfoCard({
  claimSection,
  onChange,
  editing,
  missingRequired,
  permissionUsers
}) {
  const handleSelectPrimaryContact = userId => {
    const selectedUser = permissionUsers?.find(u => u._id === userId);
    if (selectedUser) {
      onChange({
        ...claimSection,
        primaryContact: {
          ...claimSection.primaryContact,
          firstName: selectedUser.firstName || '',
          lastName: selectedUser.lastName || '',
          email: selectedUser.email || '',
          phone: {
            ...claimSection.primaryContact?.phone,
            phoneNumber: normalizePhone(selectedUser.phoneNumber) || ''
          }
        }
      });
    }
  };

  const updatePrimaryContact = (field, value) => {
    onChange({
      ...claimSection,
      primaryContact: {
        ...claimSection.primaryContact,
        [field]: value
      }
    });
  };

  const updatePrimaryContactPhone = (field, value) => {
    onChange({
      ...claimSection,
      primaryContact: {
        ...claimSection.primaryContact,
        phone: {
          ...claimSection.primaryContact.phone,
          [field]: value
        }
      }
    });
  };

  const updateReportedBy = (field, value) => {
    onChange({
      ...claimSection,
      reportedBy: {
        ...claimSection.reportedBy,
        [field]: value
      }
    });
  };

  const updateReportedByPhone = (field, value) => {
    onChange({
      ...claimSection,
      reportedBy: {
        ...claimSection.reportedBy,
        phone: {
          ...claimSection.reportedBy.phone,
          [field]: value
        }
      }
    });
  };

  const updateMailingAddress = (field, value) => {
    onChange({
      ...claimSection,
      mailingAddress: {
        ...claimSection.mailingAddress,
        [field]: value
      }
    });
  };

  const copyPrimaryToReported = () => {
    onChange({
      ...claimSection,
      reportedBy: { ...claimSection.primaryContact }
    });
  };

  if (!editing) {
    return (
      <div className={styles.viewContainer}>
        <h4>Primary Contact</h4>
        <div className={styles.viewRow}>
          <span className={styles.label}>Name:</span>
          <span className={styles.value}>
            {claimSection?.primaryContact?.firstName}{' '}
            {claimSection?.primaryContact?.lastName}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Email:</span>
          <span className={styles.value}>
            {claimSection?.primaryContact?.email || '-'}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Phone:</span>
          <span className={styles.value}>
            {claimSection?.primaryContact?.phone?.phoneNumber || '-'}
          </span>
        </div>
        <h4 style={{ marginTop: '20px' }}>Reported By</h4>
        <div className={styles.viewRow}>
          <span className={styles.label}>Name:</span>
          <span className={styles.value}>
            {claimSection?.reportedBy?.firstName}{' '}
            {claimSection?.reportedBy?.lastName}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Email:</span>
          <span className={styles.value}>
            {claimSection?.reportedBy?.email || '-'}
          </span>
        </div>
        <h4 style={{ marginTop: '20px' }}>Mailing Address</h4>
        <div className={styles.viewRow}>
          <span className={styles.label}>Address:</span>
          <span className={styles.value}>
            {claimSection?.mailingAddress?.addressLine || '-'}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>City, State, Zip:</span>
          <span className={styles.value}>
            {claimSection?.mailingAddress?.cityName},{' '}
            {claimSection?.mailingAddress?.state}{' '}
            {claimSection?.mailingAddress?.zipCode}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h4>Primary Contact</h4>
      <EmployeeDropdown
        fieldLabel="Select Employee"
        onChange={handleSelectPrimaryContact}
        searchable
        employees={permissionUsers}
        placeholder="Select an employee to populate contact info..."
      />
      <TwoColumn>
        <Textbox
          currentValue={claimSection?.primaryContact?.firstName}
          fieldLabel="First Name"
          onChange={value => updatePrimaryContact('firstName', value)}
          isRequired
          touched={missingRequired && !claimSection?.primaryContact?.firstName}
        />
        <Textbox
          currentValue={claimSection?.primaryContact?.lastName}
          fieldLabel="Last Name"
          onChange={value => updatePrimaryContact('lastName', value)}
          isRequired
          touched={missingRequired && !claimSection?.primaryContact?.lastName}
        />
        <Textbox
          currentValue={claimSection?.primaryContact?.email}
          fieldLabel="Email"
          onChange={value => updatePrimaryContact('email', value)}
          isRequired
          touched={missingRequired && !claimSection?.primaryContact?.email}
        />
        <Textbox
          currentValue={claimSection?.primaryContact?.phone?.phoneNumber}
          fieldLabel="Phone Number"
          onChange={value =>
            updatePrimaryContactPhone('phoneNumber', normalizePhone(value))
          }
          isRequired
          touched={
            missingRequired && !claimSection?.primaryContact?.phone?.phoneNumber
          }
        />
        <Dropdown
          options={PHONE_TYPES}
          currentValue={claimSection?.primaryContact?.phone?.phoneType}
          onChange={value => updatePrimaryContactPhone('phoneType', value)}
          fieldLabel="Phone Type"
        />
      </TwoColumn>

      <div style={{ marginTop: '20px', marginBottom: '10px' }}>
        <h4 style={{ display: 'inline-block', marginRight: '15px' }}>
          Reported By
        </h4>
        <Button
          text="Same as Primary Contact"
          color="blueOutline"
          onClick={copyPrimaryToReported}
        />
      </div>
      <TwoColumn>
        <Textbox
          currentValue={claimSection?.reportedBy?.firstName}
          fieldLabel="First Name"
          onChange={value => updateReportedBy('firstName', value)}
          isRequired
          touched={missingRequired && !claimSection?.reportedBy?.firstName}
        />
        <Textbox
          currentValue={claimSection?.reportedBy?.lastName}
          fieldLabel="Last Name"
          onChange={value => updateReportedBy('lastName', value)}
          isRequired
          touched={missingRequired && !claimSection?.reportedBy?.lastName}
        />
        <Textbox
          currentValue={claimSection?.reportedBy?.email}
          fieldLabel="Email"
          onChange={value => updateReportedBy('email', value)}
        />
        <Textbox
          currentValue={claimSection?.reportedBy?.phone?.phoneNumber}
          fieldLabel="Phone Number"
          onChange={value =>
            updateReportedByPhone('phoneNumber', normalizePhone(value))
          }
          isRequired
          touched={
            missingRequired && !claimSection?.reportedBy?.phone?.phoneNumber
          }
        />
        <Dropdown
          options={PHONE_TYPES}
          currentValue={claimSection?.reportedBy?.phone?.phoneType}
          onChange={value => updateReportedByPhone('phoneType', value)}
          fieldLabel="Phone Type"
        />
      </TwoColumn>

      <h4 style={{ marginTop: '20px' }}>Mailing Address</h4>
      <TwoColumn>
        <Textbox
          currentValue={claimSection?.mailingAddress?.addressLine}
          fieldLabel="Street Address"
          onChange={value => updateMailingAddress('addressLine', value)}
          isRequired
          touched={
            missingRequired && !claimSection?.mailingAddress?.addressLine
          }
        />
        <Textbox
          currentValue={claimSection?.mailingAddress?.cityName}
          fieldLabel="City"
          onChange={value => updateMailingAddress('cityName', value)}
          isRequired
          touched={missingRequired && !claimSection?.mailingAddress?.cityName}
        />
        <Dropdown
          options={states}
          currentValue={claimSection?.mailingAddress?.state}
          onChange={value => updateMailingAddress('state', value)}
          fieldLabel="State"
          isRequired
          touched={missingRequired && !claimSection?.mailingAddress?.state}
        />
        <Textbox
          currentValue={claimSection?.mailingAddress?.zipCode}
          fieldLabel="Zip Code"
          onChange={value => updateMailingAddress('zipCode', value)}
          isRequired
          touched={missingRequired && !claimSection?.mailingAddress?.zipCode}
        />
      </TwoColumn>
    </div>
  );
}

function LossLocationCard({
  claimSection,
  onChange,
  editing,
  missingRequired
}) {
  const updateAddress = (field, value) => {
    onChange({
      ...claimSection,
      address: {
        ...claimSection.address,
        [field]: value
      }
    });
  };

  if (!editing) {
    return (
      <div className={styles.viewContainer}>
        <div className={styles.viewRow}>
          <span className={styles.label}>Is Company Address:</span>
          <span className={styles.value}>
            {claimSection?.isCompanyAddress ? 'Yes' : 'No'}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Street Address:</span>
          <span className={styles.value}>
            {claimSection?.address?.addressLine || '-'}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>City:</span>
          <span className={styles.value}>
            {claimSection?.address?.cityName || '-'}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>State:</span>
          <span className={styles.value}>
            {claimSection?.address?.state || '-'}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Zip Code:</span>
          <span className={styles.value}>
            {claimSection?.address?.zipCode || '-'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <RadioButtons
        fieldLabel="Is this a company address?"
        options={[
          { value: true, label: 'Yes' },
          { value: false, label: 'No' }
        ]}
        currentValue={claimSection?.isCompanyAddress}
        onChange={value =>
          onChange({ ...claimSection, isCompanyAddress: value })
        }
      />
      <TwoColumn>
        <Textbox
          currentValue={claimSection?.address?.addressLine}
          fieldLabel="Street Address"
          onChange={value => updateAddress('addressLine', value)}
          isRequired
          touched={missingRequired && !claimSection?.address?.addressLine}
        />
        <Textbox
          currentValue={claimSection?.address?.cityName}
          fieldLabel="City"
          onChange={value => updateAddress('cityName', value)}
          isRequired
          touched={missingRequired && !claimSection?.address?.cityName}
        />
        <Dropdown
          options={states}
          currentValue={claimSection?.address?.state}
          onChange={value => updateAddress('state', value)}
          fieldLabel="State"
          isRequired
          touched={missingRequired && !claimSection?.address?.state}
        />
        <Textbox
          currentValue={claimSection?.address?.zipCode}
          fieldLabel="Zip Code"
          onChange={value => updateAddress('zipCode', value)}
          isRequired
          touched={missingRequired && !claimSection?.address?.zipCode}
        />
      </TwoColumn>
    </div>
  );
}

function LossDetailsCard({
  claimSection,
  onChange,
  editing,
  missingRequired,
  dateOfLoss,
  setDateOfLoss,
  locationCode,
  setLocationCode,
  locationCodes
}) {
  const codes = locationCodes?.map(code => ({
    value: code._id,
    label: `${code.organizationCode} - ${code.organizationText}`
  }));

  const updateVehicleAccidentDetails = (field, value) => {
    onChange({
      ...claimSection,
      vehicleAccidentDetails: {
        ...claimSection.vehicleAccidentDetails,
        [field]: value
      }
    });
  };

  if (!editing) {
    return (
      <div className={styles.viewContainer}>
        <div className={styles.viewRow}>
          <span className={styles.label}>Date of Loss:</span>
          <span className={styles.value}>{formatDate(dateOfLoss) || '-'}</span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Location Code:</span>
          <span className={styles.value}>
            {getDropdownAnswer(codes, locationCode)}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Description:</span>
          <span className={styles.value}>
            {claimSection?.description || '-'}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Vehicles Involved:</span>
          <span className={styles.value}>
            {claimSection?.vehicleAccidentDetails?.vehiclesInvolved || '-'}
          </span>
        </div>
        {parseInt(claimSection?.vehicleAccidentDetails?.vehiclesInvolved) ===
          1 && (
          <div className={styles.viewRow}>
            <span className={styles.label}>Accident Type:</span>
            <span className={styles.value}>
              {SINGLE_VEHICLE_ACCIDENT_TYPES.find(
                t =>
                  t.value ===
                  claimSection?.vehicleAccidentDetails
                    ?.singleVehicleAccidentType
              )?.label || '-'}
            </span>
          </div>
        )}
        <div className={styles.viewRow}>
          <span className={styles.label}>Has Injured Passengers:</span>
          <span className={styles.value}>
            {claimSection?.hasInjuredPassengers ? 'Yes' : 'No'}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Primary Vehicle Damaged:</span>
          <span className={styles.value}>
            {claimSection?.primaryVehicleDamaged ? 'Yes' : 'No'}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Primary Vehicle Drivable:</span>
          <span className={styles.value}>
            {claimSection?.primaryVehicleDrivable ? 'Yes' : 'No'}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Ownership Type:</span>
          <span className={styles.value}>
            {OWNERSHIP_TYPES.find(
              o => o.value === claimSection?.ownershipTypeCode
            )?.label || '-'}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Incident Only:</span>
          <span className={styles.value}>
            {claimSection?.incidentOnly ? 'Yes' : 'No'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <TwoColumn>
        <IReportDatePicker
          fieldLabel="Date of Loss"
          name="dateOfLoss"
          currentValue={dateOfLoss}
          onChange={setDateOfLoss}
          isRequired
          touched={missingRequired && !dateOfLoss}
        />
        <Dropdown
          searchable
          options={codes}
          currentValue={locationCode}
          fieldLabel="Location Code"
          onChange={setLocationCode}
          isRequired
          touched={missingRequired && !locationCode}
        />
      </TwoColumn>
      <Textbox
        currentValue={claimSection?.description}
        fieldLabel="Description of Accident"
        onChange={value => onChange({ ...claimSection, description: value })}
        isRequired
        touched={missingRequired && !claimSection?.description}
        type="textarea"
      />
      <TwoColumn>
        <Textbox
          currentValue={claimSection?.vehicleAccidentDetails?.vehiclesInvolved}
          fieldLabel="Number of Vehicles Involved"
          onChange={value =>
            updateVehicleAccidentDetails(
              'vehiclesInvolved',
              parseInt(value) || 2
            )
          }
          type="number"
          placeholder="Minimum 2 for multi-vehicle accidents"
        />
        {parseInt(claimSection?.vehicleAccidentDetails?.vehiclesInvolved) ===
          1 && (
          <Dropdown
            options={SINGLE_VEHICLE_ACCIDENT_TYPES}
            currentValue={
              claimSection?.vehicleAccidentDetails?.singleVehicleAccidentType
            }
            onChange={value =>
              updateVehicleAccidentDetails('singleVehicleAccidentType', value)
            }
            fieldLabel="Single Vehicle Accident Type"
            isRequired
            touched={
              missingRequired &&
              !claimSection?.vehicleAccidentDetails?.singleVehicleAccidentType
            }
          />
        )}
        <Dropdown
          options={OWNERSHIP_TYPES}
          currentValue={claimSection?.ownershipTypeCode}
          onChange={value =>
            onChange({ ...claimSection, ownershipTypeCode: value })
          }
          fieldLabel="Vehicle Ownership Type"
          isRequired
          touched={missingRequired && !claimSection?.ownershipTypeCode}
        />
      </TwoColumn>
      <TwoColumn>
        <RadioButtons
          fieldLabel="Has Injured Passengers?"
          options={[
            { value: true, label: 'Yes' },
            { value: false, label: 'No' }
          ]}
          currentValue={claimSection?.hasInjuredPassengers}
          onChange={value =>
            onChange({ ...claimSection, hasInjuredPassengers: value })
          }
          isRequired
        />
        <RadioButtons
          fieldLabel="Incident Only (No Claim)?"
          options={[
            { value: true, label: 'Yes' },
            { value: false, label: 'No' }
          ]}
          currentValue={claimSection?.incidentOnly}
          onChange={value => onChange({ ...claimSection, incidentOnly: value })}
        />
      </TwoColumn>
      <TwoColumn>
        <RadioButtons
          fieldLabel="Primary Vehicle Damaged?"
          options={[
            { value: true, label: 'Yes' },
            { value: false, label: 'No' }
          ]}
          currentValue={claimSection?.primaryVehicleDamaged}
          onChange={value =>
            onChange({ ...claimSection, primaryVehicleDamaged: value })
          }
        />
        <RadioButtons
          fieldLabel="Primary Vehicle Drivable?"
          options={[
            { value: true, label: 'Yes' },
            { value: false, label: 'No' }
          ]}
          currentValue={claimSection?.primaryVehicleDrivable}
          onChange={value =>
            onChange({ ...claimSection, primaryVehicleDrivable: value })
          }
        />
      </TwoColumn>
    </div>
  );
}

function PrimaryVehicleCard({
  claimSection,
  onChange,
  editing,
  missingRequired
}) {
  const updateOwner = (field, value) => {
    onChange({
      ...claimSection,
      owner: {
        ...claimSection.owner,
        [field]: value
      }
    });
  };

  const updateOwnerAddress = (field, value) => {
    onChange({
      ...claimSection,
      owner: {
        ...claimSection.owner,
        address: {
          ...claimSection.owner.address,
          [field]: value
        }
      }
    });
  };

  const updateOwnerPhone = (field, value) => {
    onChange({
      ...claimSection,
      owner: {
        ...claimSection.owner,
        phone: {
          ...claimSection.owner.phone,
          [field]: value
        }
      }
    });
  };

  if (!editing) {
    return (
      <div className={styles.viewContainer}>
        <h4>Vehicle Details</h4>
        <div className={styles.viewRow}>
          <span className={styles.label}>Year:</span>
          <span className={styles.value}>{claimSection?.year || '-'}</span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Make:</span>
          <span className={styles.value}>{claimSection?.make || '-'}</span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Model:</span>
          <span className={styles.value}>{claimSection?.model || '-'}</span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>VIN:</span>
          <span className={styles.value}>{claimSection?.vin || '-'}</span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Vehicle Type:</span>
          <span className={styles.value}>
            {VEHICLE_TYPES.find(v => v.value === claimSection?.type)?.label ||
              '-'}
          </span>
        </div>
        <h4 style={{ marginTop: '20px' }}>Owner Information</h4>
        <div className={styles.viewRow}>
          <span className={styles.label}>Name:</span>
          <span className={styles.value}>
            {claimSection?.owner?.firstName} {claimSection?.owner?.lastName}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Email:</span>
          <span className={styles.value}>
            {claimSection?.owner?.email || '-'}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Phone:</span>
          <span className={styles.value}>
            {claimSection?.owner?.phone?.phoneNumber || '-'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h4>Vehicle Details</h4>
      <TwoColumn>
        <Textbox
          currentValue={claimSection?.year}
          fieldLabel="Year"
          onChange={value => onChange({ ...claimSection, year: value })}
          isRequired
          touched={missingRequired && !claimSection?.year}
        />
        <Textbox
          currentValue={claimSection?.make}
          fieldLabel="Make"
          onChange={value => onChange({ ...claimSection, make: value })}
          isRequired
          touched={missingRequired && !claimSection?.make}
        />
        <Textbox
          currentValue={claimSection?.model}
          fieldLabel="Model"
          onChange={value => onChange({ ...claimSection, model: value })}
          isRequired
          touched={missingRequired && !claimSection?.model}
        />
        <Textbox
          currentValue={claimSection?.vin}
          fieldLabel="VIN"
          onChange={value => onChange({ ...claimSection, vin: value })}
          placeholder="17-character VIN"
          isRequired
          touched={
            missingRequired &&
            (!claimSection?.vin || claimSection?.vin?.length !== 17)
          }
          error={
            claimSection?.vin && claimSection?.vin?.length !== 17
              ? 'VIN must be exactly 17 characters'
              : null
          }
        />
        <Dropdown
          options={VEHICLE_TYPES}
          currentValue={claimSection?.type}
          onChange={value => onChange({ ...claimSection, type: value })}
          fieldLabel="Vehicle Type"
        />
      </TwoColumn>

      <h4 style={{ marginTop: '20px' }}>Owner Information</h4>
      <TwoColumn>
        <Textbox
          currentValue={claimSection?.owner?.firstName}
          fieldLabel="First Name"
          onChange={value => updateOwner('firstName', value)}
          isRequired
          touched={missingRequired && !claimSection?.owner?.firstName}
        />
        <Textbox
          currentValue={claimSection?.owner?.lastName}
          fieldLabel="Last Name"
          onChange={value => updateOwner('lastName', value)}
          isRequired
          touched={missingRequired && !claimSection?.owner?.lastName}
        />
        <Textbox
          currentValue={claimSection?.owner?.email}
          fieldLabel="Email"
          onChange={value => updateOwner('email', value)}
          isRequired
          touched={missingRequired && !claimSection?.owner?.email}
        />
        <Textbox
          currentValue={claimSection?.owner?.phone?.phoneNumber}
          fieldLabel="Phone Number"
          onChange={value =>
            updateOwnerPhone('phoneNumber', normalizePhone(value))
          }
          isRequired
          touched={missingRequired && !claimSection?.owner?.phone?.phoneNumber}
        />
        <Textbox
          currentValue={claimSection?.owner?.phone?.phoneExtn}
          fieldLabel="Extension"
          onChange={value => updateOwnerPhone('phoneExtn', value)}
        />
        <Dropdown
          options={PHONE_TYPES}
          currentValue={claimSection?.owner?.phone?.phoneType}
          onChange={value => updateOwnerPhone('phoneType', value)}
          fieldLabel="Phone Type"
        />
      </TwoColumn>
      <TwoColumn>
        <Textbox
          currentValue={claimSection?.owner?.address?.addressLine}
          fieldLabel="Street Address"
          onChange={value => updateOwnerAddress('addressLine', value)}
          isRequired
          touched={
            missingRequired && !claimSection?.owner?.address?.addressLine
          }
        />
        <Textbox
          currentValue={claimSection?.owner?.address?.cityName}
          fieldLabel="City"
          onChange={value => updateOwnerAddress('cityName', value)}
          isRequired
          touched={missingRequired && !claimSection?.owner?.address?.cityName}
        />
        <Dropdown
          options={states}
          currentValue={claimSection?.owner?.address?.state}
          onChange={value => updateOwnerAddress('state', value)}
          fieldLabel="State"
          isRequired
          touched={missingRequired && !claimSection?.owner?.address?.state}
        />
        <Textbox
          currentValue={claimSection?.owner?.address?.zipCode}
          fieldLabel="Zip Code"
          onChange={value => updateOwnerAddress('zipCode', value)}
          isRequired
          touched={missingRequired && !claimSection?.owner?.address?.zipCode}
        />
      </TwoColumn>
    </div>
  );
}

function DriverInfoCard({ claimSection, onChange, editing, missingRequired }) {
  const updateAddress = (field, value) => {
    onChange({
      ...claimSection,
      address: {
        ...claimSection.address,
        [field]: value
      }
    });
  };

  const updatePhone = (field, value) => {
    onChange({
      ...claimSection,
      phone: {
        ...claimSection.phone,
        [field]: value
      }
    });
  };

  if (!editing) {
    return (
      <div className={styles.viewContainer}>
        <div className={styles.viewRow}>
          <span className={styles.label}>Name:</span>
          <span className={styles.value}>
            {claimSection?.firstName} {claimSection?.lastName}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Email:</span>
          <span className={styles.value}>{claimSection?.email || '-'}</span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Phone:</span>
          <span className={styles.value}>
            {claimSection?.phone?.phoneNumber || '-'}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Is Driver Injured:</span>
          <span className={styles.value}>
            {claimSection?.isDriverInjured ? 'Yes' : 'No'}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Injury Type:</span>
          <span className={styles.value}>
            {INJURY_TYPES.find(i => i.value === claimSection?.injuryType)
              ?.label || '-'}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>Address:</span>
          <span className={styles.value}>
            {claimSection?.address?.addressLine || '-'}
          </span>
        </div>
        <div className={styles.viewRow}>
          <span className={styles.label}>City, State, Zip:</span>
          <span className={styles.value}>
            {claimSection?.address?.cityName}, {claimSection?.address?.state}{' '}
            {claimSection?.address?.zipCode}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <TwoColumn>
        <Textbox
          currentValue={claimSection?.firstName}
          fieldLabel="First Name"
          onChange={value => onChange({ ...claimSection, firstName: value })}
          isRequired
          touched={missingRequired && !claimSection?.firstName}
        />
        <Textbox
          currentValue={claimSection?.lastName}
          fieldLabel="Last Name"
          onChange={value => onChange({ ...claimSection, lastName: value })}
          isRequired
          touched={missingRequired && !claimSection?.lastName}
        />
        <Textbox
          currentValue={claimSection?.email}
          fieldLabel="Email"
          onChange={value => onChange({ ...claimSection, email: value })}
        />
        <Textbox
          currentValue={claimSection?.phone?.phoneNumber}
          fieldLabel="Phone Number"
          onChange={value => updatePhone('phoneNumber', normalizePhone(value))}
          isRequired
          touched={missingRequired && !claimSection?.phone?.phoneNumber}
        />
        <Textbox
          currentValue={claimSection?.phone?.phoneExtn}
          fieldLabel="Extension"
          onChange={value => updatePhone('phoneExtn', value)}
        />
        <Dropdown
          options={PHONE_TYPES}
          currentValue={claimSection?.phone?.phoneType}
          onChange={value => updatePhone('phoneType', value)}
          fieldLabel="Phone Type"
        />
      </TwoColumn>
      <TwoColumn>
        <RadioButtons
          fieldLabel="Is Driver Injured?"
          options={[
            { value: true, label: 'Yes' },
            { value: false, label: 'No' }
          ]}
          currentValue={claimSection?.isDriverInjured}
          onChange={value =>
            onChange({ ...claimSection, isDriverInjured: value })
          }
        />
        {claimSection?.isDriverInjured && (
          <Dropdown
            options={INJURY_TYPES}
            currentValue={claimSection?.injuryType}
            onChange={value => onChange({ ...claimSection, injuryType: value })}
            fieldLabel="Injury Type"
          />
        )}
      </TwoColumn>
      <h4 style={{ marginTop: '20px' }}>Driver Address</h4>
      <TwoColumn>
        <Textbox
          currentValue={claimSection?.address?.addressLine}
          fieldLabel="Street Address"
          onChange={value => updateAddress('addressLine', value)}
          isRequired
          touched={missingRequired && !claimSection?.address?.addressLine}
        />
        <Textbox
          currentValue={claimSection?.address?.cityName}
          fieldLabel="City"
          onChange={value => updateAddress('cityName', value)}
          isRequired
          touched={missingRequired && !claimSection?.address?.cityName}
        />
        <Dropdown
          options={states}
          currentValue={claimSection?.address?.state}
          onChange={value => updateAddress('state', value)}
          fieldLabel="State"
          isRequired
          touched={missingRequired && !claimSection?.address?.state}
        />
        <Textbox
          currentValue={claimSection?.address?.zipCode}
          fieldLabel="Zip Code"
          onChange={value => updateAddress('zipCode', value)}
          isRequired
          touched={missingRequired && !claimSection?.address?.zipCode}
        />
      </TwoColumn>
    </div>
  );
}

function ClaimantsCard({ claimSection, onChange, editing, missingRequired }) {
  const claimants = Array.isArray(claimSection) ? claimSection : [];

  const addClaimant = type => {
    const newClaimant = {
      claimantType: type,
      ...(type === 'P'
        ? {
            businessName: '',
            description: '',
            email: '',
            address: { addressLine: '', cityName: '', state: '', zipCode: '' }
          }
        : type === 'B'
          ? {
              firstName: '',
              lastName: '',
              email: '',
              injuryType: '',
              relationshipToInsured: '',
              phone: { phoneNumber: '', phoneExtn: '', phoneType: 'M' },
              address: { addressLine: '', cityName: '', state: '', zipCode: '' }
            }
          : {
              make: '',
              model: '',
              year: '',
              owner: {
                firstName: '',
                lastName: '',
                email: '',
                phone: { phoneNumber: '', phoneExtn: '', phoneType: 'M' },
                address: {
                  addressLine: '',
                  cityName: '',
                  state: '',
                  zipCode: ''
                }
              }
            })
    };
    onChange([...claimants, newClaimant]);
  };

  const updateClaimant = (index, field, value) => {
    const updated = [...claimants];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const updateClaimantNested = (index, path, value) => {
    const updated = [...claimants];
    const keys = path.split('.');
    let obj = updated[index];
    for (let i = 0; i < keys.length - 1; i++) {
      obj[keys[i]] = { ...obj[keys[i]] };
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;
    onChange(updated);
  };

  const removeClaimant = index => {
    const updated = claimants.filter((_, i) => i !== index);
    onChange(updated);
  };

  if (!editing) {
    return (
      <div className={styles.viewContainer}>
        {claimants.length === 0 ? (
          <p>No claimants added.</p>
        ) : (
          claimants.map((claimant, index) => (
            <div
              key={index}
              style={{
                marginBottom: '20px',
                paddingBottom: '20px',
                borderBottom: '1px solid #eee'
              }}
            >
              <h4>
                Claimant {index + 1} -{' '}
                {CLAIMANT_TYPES.find(c => c.value === claimant.claimantType)
                  ?.label || 'Unknown'}
              </h4>
              {claimant.claimantType === 'P' && (
                <>
                  <div className={styles.viewRow}>
                    <span className={styles.label}>Business Name:</span>
                    <span className={styles.value}>
                      {claimant.businessName || '-'}
                    </span>
                  </div>
                  <div className={styles.viewRow}>
                    <span className={styles.label}>Description:</span>
                    <span className={styles.value}>
                      {claimant.description || '-'}
                    </span>
                  </div>
                </>
              )}
              {claimant.claimantType === 'B' && (
                <>
                  <div className={styles.viewRow}>
                    <span className={styles.label}>Name:</span>
                    <span className={styles.value}>
                      {claimant.firstName} {claimant.lastName}
                    </span>
                  </div>
                  <div className={styles.viewRow}>
                    <span className={styles.label}>Injury Type:</span>
                    <span className={styles.value}>
                      {INJURY_TYPES.find(i => i.value === claimant.injuryType)
                        ?.label || '-'}
                    </span>
                  </div>
                </>
              )}
              {claimant.claimantType === 'A' && (
                <>
                  <div className={styles.viewRow}>
                    <span className={styles.label}>Vehicle:</span>
                    <span className={styles.value}>
                      {claimant.year} {claimant.make} {claimant.model}
                    </span>
                  </div>
                  <div className={styles.viewRow}>
                    <span className={styles.label}>Owner:</span>
                    <span className={styles.value}>
                      {claimant.owner?.firstName} {claimant.owner?.lastName}
                    </span>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <Button
          text="+ Property Damage"
          color="blueOutline"
          onClick={() => addClaimant('P')}
        />
        <Button
          text="+ Bodily Injury"
          color="blueOutline"
          onClick={() => addClaimant('B')}
          style={{ marginLeft: '10px' }}
        />
        <Button
          text="+ Other Vehicle"
          color="blueOutline"
          onClick={() => addClaimant('A')}
          style={{ marginLeft: '10px' }}
        />
      </div>

      {claimants.map((claimant, index) => (
        <div
          key={index}
          style={{
            marginBottom: '20px',
            padding: '15px',
            border: '1px solid #ddd',
            borderRadius: '8px'
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px'
            }}
          >
            <h4 style={{ margin: 0 }}>
              Claimant {index + 1} -{' '}
              {CLAIMANT_TYPES.find(c => c.value === claimant.claimantType)
                ?.label || 'Unknown'}
            </h4>
            <Button
              text="Remove"
              color="redOutline"
              onClick={() => removeClaimant(index)}
            />
          </div>

          {claimant.claimantType === 'P' && (
            <PropertyDamageClaimant
              claimant={claimant}
              updateClaimant={(field, value) =>
                updateClaimant(index, field, value)
              }
              updateClaimantNested={(path, value) =>
                updateClaimantNested(index, path, value)
              }
            />
          )}
          {claimant.claimantType === 'B' && (
            <BodilyInjuryClaimant
              claimant={claimant}
              updateClaimant={(field, value) =>
                updateClaimant(index, field, value)
              }
              updateClaimantNested={(path, value) =>
                updateClaimantNested(index, path, value)
              }
            />
          )}
          {claimant.claimantType === 'A' && (
            <OtherVehicleClaimant
              claimant={claimant}
              updateClaimant={(field, value) =>
                updateClaimant(index, field, value)
              }
              updateClaimantNested={(path, value) =>
                updateClaimantNested(index, path, value)
              }
            />
          )}
        </div>
      ))}

      {claimants.length === 0 && (
        <p style={{ color: '#666', fontStyle: 'italic' }}>
          No claimants added yet. Use the buttons above to add property damage,
          bodily injury, or other vehicle claimants.
        </p>
      )}
    </div>
  );
}

function PropertyDamageClaimant({
  claimant,
  updateClaimant,
  updateClaimantNested
}) {
  return (
    <div>
      <TwoColumn>
        <Textbox
          currentValue={claimant.businessName}
          fieldLabel="Business/Property Name"
          onChange={value => updateClaimant('businessName', value)}
        />
        <Textbox
          currentValue={claimant.email}
          fieldLabel="Email"
          onChange={value => updateClaimant('email', value)}
        />
      </TwoColumn>
      <Textbox
        currentValue={claimant.description}
        fieldLabel="Description of Damage"
        onChange={value => updateClaimant('description', value)}
        type="textarea"
      />
      <TwoColumn>
        <Textbox
          currentValue={claimant.address?.addressLine}
          fieldLabel="Street Address"
          onChange={value => updateClaimantNested('address.addressLine', value)}
        />
        <Textbox
          currentValue={claimant.address?.cityName}
          fieldLabel="City"
          onChange={value => updateClaimantNested('address.cityName', value)}
        />
        <Dropdown
          options={states}
          currentValue={claimant.address?.state}
          onChange={value => updateClaimantNested('address.state', value)}
          fieldLabel="State"
        />
        <Textbox
          currentValue={claimant.address?.zipCode}
          fieldLabel="Zip Code"
          onChange={value => updateClaimantNested('address.zipCode', value)}
        />
      </TwoColumn>
    </div>
  );
}

function BodilyInjuryClaimant({
  claimant,
  updateClaimant,
  updateClaimantNested
}) {
  return (
    <div>
      <TwoColumn>
        <Textbox
          currentValue={claimant.firstName}
          fieldLabel="First Name"
          onChange={value => updateClaimant('firstName', value)}
        />
        <Textbox
          currentValue={claimant.lastName}
          fieldLabel="Last Name"
          onChange={value => updateClaimant('lastName', value)}
        />
        <Textbox
          currentValue={claimant.email}
          fieldLabel="Email"
          onChange={value => updateClaimant('email', value)}
        />
        <Textbox
          currentValue={claimant.phone?.phoneNumber}
          fieldLabel="Phone Number"
          onChange={value =>
            updateClaimantNested('phone.phoneNumber', normalizePhone(value))
          }
        />
        <Dropdown
          options={INJURY_TYPES}
          currentValue={claimant.injuryType}
          onChange={value => updateClaimant('injuryType', value)}
          fieldLabel="Injury Type"
        />
        <Dropdown
          options={RELATIONSHIP_TO_INSURED}
          currentValue={claimant.relationshipToInsured}
          onChange={value => updateClaimant('relationshipToInsured', value)}
          fieldLabel="Relationship to Insured"
        />
      </TwoColumn>
      <TwoColumn>
        <Textbox
          currentValue={claimant.address?.addressLine}
          fieldLabel="Street Address"
          onChange={value => updateClaimantNested('address.addressLine', value)}
        />
        <Textbox
          currentValue={claimant.address?.cityName}
          fieldLabel="City"
          onChange={value => updateClaimantNested('address.cityName', value)}
        />
        <Dropdown
          options={states}
          currentValue={claimant.address?.state}
          onChange={value => updateClaimantNested('address.state', value)}
          fieldLabel="State"
        />
        <Textbox
          currentValue={claimant.address?.zipCode}
          fieldLabel="Zip Code"
          onChange={value => updateClaimantNested('address.zipCode', value)}
        />
      </TwoColumn>
    </div>
  );
}

function OtherVehicleClaimant({
  claimant,
  updateClaimant,
  updateClaimantNested
}) {
  return (
    <div>
      <h5>Vehicle Information</h5>
      <TwoColumn>
        <Textbox
          currentValue={claimant.year}
          fieldLabel="Year"
          onChange={value => updateClaimant('year', value)}
        />
        <Textbox
          currentValue={claimant.make}
          fieldLabel="Make"
          onChange={value => updateClaimant('make', value)}
        />
        <Textbox
          currentValue={claimant.model}
          fieldLabel="Model"
          onChange={value => updateClaimant('model', value)}
        />
      </TwoColumn>
      <h5 style={{ marginTop: '15px' }}>Owner Information</h5>
      <TwoColumn>
        <Textbox
          currentValue={claimant.owner?.firstName}
          fieldLabel="First Name"
          onChange={value => updateClaimantNested('owner.firstName', value)}
        />
        <Textbox
          currentValue={claimant.owner?.lastName}
          fieldLabel="Last Name"
          onChange={value => updateClaimantNested('owner.lastName', value)}
        />
        <Textbox
          currentValue={claimant.owner?.email}
          fieldLabel="Email"
          onChange={value => updateClaimantNested('owner.email', value)}
        />
        <Textbox
          currentValue={claimant.owner?.phone?.phoneNumber}
          fieldLabel="Phone Number"
          onChange={value =>
            updateClaimantNested(
              'owner.phone.phoneNumber',
              normalizePhone(value)
            )
          }
        />
      </TwoColumn>
      <TwoColumn>
        <Textbox
          currentValue={claimant.owner?.address?.addressLine}
          fieldLabel="Street Address"
          onChange={value =>
            updateClaimantNested('owner.address.addressLine', value)
          }
        />
        <Textbox
          currentValue={claimant.owner?.address?.cityName}
          fieldLabel="City"
          onChange={value =>
            updateClaimantNested('owner.address.cityName', value)
          }
        />
        <Dropdown
          options={states}
          currentValue={claimant.owner?.address?.state}
          onChange={value => updateClaimantNested('owner.address.state', value)}
          fieldLabel="State"
        />
        <Textbox
          currentValue={claimant.owner?.address?.zipCode}
          fieldLabel="Zip Code"
          onChange={value =>
            updateClaimantNested('owner.address.zipCode', value)
          }
        />
      </TwoColumn>
    </div>
  );
}
